import { displayName } from '../../domain/names';
import type { UUID } from '../../domain/types';
import { idbKeys } from '../../lib/idb';
import { nowISO } from '../../lib/time';
import { uuid } from '../../lib/uuid';
import type { PictureCheck, PictureCheckItem, PrototypeControls, Repositories, SimulationSettings } from '../types';
import { deleteBlob, getBlob, mediumKey, portraitThumbKey, thumbKey } from './blobStore';
import { MockAdminRepository } from './MockAdminRepository';
import { MockAuthRepository } from './MockAuthRepository';
import { MockDb } from './MockDb';
import { MockFamilyRepository } from './MockFamilyRepository';
import { MockFeedbackRepository } from './MockFeedbackRepository';
import { MockMediaRepository } from './MockMediaRepository';
import { MockPersonRepository } from './MockPersonRepository';
import { MockPhotoRepository } from './MockPhotoRepository';
import { MockPlaceRepository } from './MockPlaceRepository';
import { MockRecipeRepository } from './MockRecipeRepository';
import { MockRelationshipRepository } from './MockRelationshipRepository';
import { MockStoryRepository } from './MockStoryRepository';
import { MockSyncRepository } from './MockSyncRepository';
import { seedDatabase } from './seed';
import { A, P } from './seed/people';
import { S } from './seed/stories';

class PrototypeControlsImpl implements PrototypeControls {
  constructor(private db: MockDb) {}

  getSettings(): SimulationSettings {
    return this.db.getSim();
  }

  setSettings(patch: Partial<SimulationSettings>): void {
    this.db.setSim(patch);
  }

  onSettings(listener: (s: SimulationSettings) => void): () => void {
    return this.db.onSim(listener);
  }

  async resetDemoData(): Promise<void> {
    await this.db.resetAll();
  }

  /** "David" changes a field on the given person so the next save from this tab conflicts (section 16.4). */
  async simulateOtherEditor(personId: UUID): Promise<{ by: string; field: string; value: unknown }> {
    await this.db.ready;
    const p = this.db.t.persons.find((x) => x.id === personId);
    if (!p) throw new Error('person not found');
    const editor = this.db.t.accounts.find((a) => a.id === A('david')) ?? this.db.t.accounts[0];
    const me = this.db.getSession()?.accountId;
    const by = editor.id === me ? (this.db.t.accounts.find((a) => a.id === A('sarah')) ?? editor) : editor;
    const before = { notes: p.notes };
    const value = `Checked by ${by.displayName} on ${new Date().toLocaleDateString()}`;
    p.notes = value;
    p.version += 1;
    p.updatedAt = nowISO();
    p.updatedBy = by.id;
    this.db.audit({ actorAccountId: by.id, entityType: 'person', entityId: p.id, action: 'update', before, after: { notes: value }, description: 'Updated notes (simulated concurrent editor)', entityVersion: p.version });
    await this.db.commit(['persons', 'audit_events'], [p.id]);
    return { by: by.displayName, field: 'notes', value };
  }

  /** Another family member publishes a story right now (memory dot + timeline update). */
  async simulateIncomingStory(): Promise<void> {
    await this.db.ready;
    const now = nowISO();
    const id = uuid();
    const teller = P('leah');
    this.db.t.stories.push({
      id, familyId: this.db.familyId(), title: 'The picnic at the Carmel', titlePending: false, date: { kind: 'about', start: '1992', certainty: 'approximate' },
      status: 'published', creatorAccountId: A('david'), primaryStorytellerPersonId: teller, publishedAt: now, locked: false,
      idempotencyKey: `sim:${id}`, version: 1, createdAt: now, updatedAt: now, updatedBy: A('david'),
    });
    this.db.t.story_contributions.push({ id: uuid(), storyId: id, speakerPersonId: teller, uploaderAccountId: A('david'), order: 0, kind: 'text', text: 'We drove up to the Carmel with three cars and one watermelon. Yonatan was six and insisted on carrying it himself.', status: 'published', idempotencyKey: `sim:${id}:c`, version: 1, createdAt: now, updatedAt: now, updatedBy: A('david') });
    this.db.t.story_people.push({ id: uuid(), storyId: id, personId: teller, role: 'storyteller' }, { id: uuid(), storyId: id, personId: P('yonatan'), role: 'subject' });
    this.db.audit({ actorAccountId: A('david'), entityType: 'story', entityId: id, action: 'publish', description: 'Published “The picnic at the Carmel” (simulated)' });
    await this.db.commit(['stories', 'story_contributions', 'story_people', 'audit_events'], [id, teller]);
    void S;
  }

  async exportJson(): Promise<string> {
    await this.db.ready;
    return JSON.stringify(this.db.t, null, 2);
  }

  /**
   * Every picture, and whether it is really there.
   *
   * A photograph in this prototype is two things in two places: a row in
   * `person_portraits` or `family_photos`, and the bytes under its `storageKey`
   * in the blob store. They are written together and they can still come apart —
   * a write that failed, a database cleared while a tab was open, a browser that
   * evicted one store. When a picture will not draw, which of the two is missing
   * is the only question worth asking, and this answers it rather than leaving it
   * to be guessed at over a chat.
   */
  async checkPictures(): Promise<PictureCheck> {
    await this.db.ready;
    const broken: PictureCheckItem[] = [];
    let bytes = 0;
    const wanted = new Set<string>();

    const look = async (kind: 'portrait' | 'photo', id: UUID, label: string, storageKey: string) => {
      wanted.add(storageKey);
      const blob = await getBlob(storageKey);
      // getBlob already treats a zero-byte blob as missing, so anything that
      // comes back undefined is one of the two problems; the size tells them apart.
      if (!blob) {
        broken.push({ kind, id, label, storageKey, problem: 'missing' });
        return;
      }
      if (blob.size === 0) {
        broken.push({ kind, id, label, storageKey, bytes: 0, problem: 'empty' });
        return;
      }
      bytes += blob.size;
    };

    const nameOf = (personId: UUID) => {
      const p = this.db.t.persons.find((x) => x.id === personId);
      const names = p ? this.db.t.person_names.filter((n) => n.personId === p.id) : [];
      return p ? displayName({ ...p, names }, 'en') : personId.slice(0, 8);
    };

    const portraits = this.db.t.person_portraits.filter((p) => !p.removed);
    for (const p of portraits) await look('portrait', p.id, nameOf(p.personId), p.storageKey);

    const photos = this.db.t.family_photos.filter((p) => !p.removed && !!p.storageKey);
    for (const p of photos) await look('photo', p.id, p.title || p.caption || 'A picture', p.storageKey!);

    // A thumbnail belongs to the picture it was made from; anything else under a
    // key nothing points at is left over from something that has gone.
    let keys: string[] = [];
    try {
      keys = await idbKeys('blobs');
    } catch {
      /* storage unreadable; the counts above are what we have */
    }
    const liveThumbs = new Set([...photos.map((p) => thumbKey(p.id)), ...photos.map((p) => mediumKey(p.id)), ...portraits.map((p) => portraitThumbKey(p.id))]);
    const orphans = keys.filter((k) => {
      if (wanted.has(k)) return false;
      if (k.startsWith('thumb/')) return !liveThumbs.has(k);
      // Recordings and their chunks belong to the media repository, not here.
      return !k.startsWith('media/') && !k.startsWith('chunk/');
    });

    return {
      people: this.db.t.persons.filter((p) => !p.archived && !p.mergedIntoId).length,
      portraits: portraits.length,
      photos: photos.length,
      broken,
      orphans,
      bytes,
      persistent: this.db.isPersistent(),
    };
  }

  async repairPictures(): Promise<{ portraits: number; photos: number; thumbs: number }> {
    const check = await this.checkPictures();
    let portraits = 0;
    let photos = 0;
    for (const item of check.broken) {
      if (item.kind === 'portrait') {
        const row = this.db.t.person_portraits.find((p) => p.id === item.id);
        if (!row) continue;
        this.db.t.person_portraits = this.db.t.person_portraits.filter((p) => p.id !== item.id);
        portraits++;
        // The circle was showing this one: fall back to the newest face that is
        // still readable, and to initials when there is none left.
        const person = this.db.t.persons.find((p) => p.id === row.personId);
        if (person && person.portraitUrl === `portrait:${row.id}`) {
          const left = this.db.t.person_portraits
            .filter((p) => p.personId === row.personId && !p.removed && !check.broken.some((b) => b.id === p.id))
            .sort((a, b) => b.createdAt.localeCompare(a.createdAt))[0];
          for (const p of this.db.t.person_portraits) if (p.personId === row.personId) p.current = !!left && p.id === left.id;
          person.portraitUrl = left ? `portrait:${left.id}` : undefined;
        }
      } else {
        const row = this.db.t.family_photos.find((p) => p.id === item.id);
        if (!row) continue;
        // A Moment can be a day with no picture in it (a milestone, an event), so
        // one whose blob is gone keeps its row and simply stops claiming a photo.
        row.storageKey = undefined;
        row.mimeType = undefined;
        row.bytes = undefined;
        photos++;
      }
    }
    for (const key of check.orphans) await deleteBlob(key);
    if (portraits || photos) await this.db.commit(['person_portraits', 'persons', 'family_photos']);
    return { portraits, photos, thumbs: check.orphans.length };
  }
}

export function createMockRepositories(): Repositories {
  const db = new MockDb(seedDatabase);
  const people = new MockPersonRepository(db);
  const media = new MockMediaRepository(db);
  const repos: Repositories = {
    auth: new MockAuthRepository(db),
    photos: new MockPhotoRepository(db),
    recipes: new MockRecipeRepository(db),
    places: new MockPlaceRepository(db),
    family: new MockFamilyRepository(db),
    people,
    relationships: new MockRelationshipRepository(db, people),
    stories: new MockStoryRepository(db, media),
    media,
    sync: new MockSyncRepository(db, people),
    admin: new MockAdminRepository(db, people, media),
    feedback: new MockFeedbackRepository(db),
    prototype: new PrototypeControlsImpl(db),
  };
  void db.ready.then(() => media.resumePendingUploads());
  return repos;
}
